import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/envago/Icon/Icon';
import { AiReadingBlocker, AiReadingStep, NoMatchInfo } from '../aiReadingMachine';

const STEP_NUMBER: Partial<Record<AiReadingStep, number>> = {
    framing: 1,
    identify: 2,
    tariffs: 3,
    read: 4,
    confirm: 4,
};

export const instructionKeyFor = (step: AiReadingStep, blocker: AiReadingBlocker, noMatch?: NoMatchInfo | null): string => {
    // The noMatch blocker speaks/shows a reason-specific explanation ("that is a gas meter, but
    // you have no gas job") instead of a generic "not found" whenever the reason is known.
    if (blocker === 'noMatch' && noMatch?.reason === 'wrongMedium') {
        return 'aiReading.instructions.noMatchWrongMedium';
    }
    if (blocker === 'noMatch' && noMatch?.reason === 'numberMismatch' && noMatch.detectedNumber) {
        return 'aiReading.instructions.noMatchNumber';
    }
    if (blocker) {
        return `aiReading.instructions.${blocker}`;
    }
    return `aiReading.instructions.${step}`;
};

// Interpolation values for the reason-specific noMatch instructions ({{medium}}, {{number}}).
// Shared by GuidancePanel, the spoken announcement and MeterNotFoundPanel so the user hears and
// reads the exact same sentence.
export const instructionParamsFor = (noMatch: NoMatchInfo | null | undefined, t: (key: string) => string): Record<string, string> | undefined =>
    noMatch
        ? {
              number: noMatch.detectedNumber ?? '',
              medium: t(`aiReading.medium.${noMatch.detectedMedium ?? 'ABSTRACT'}`),
          }
        : undefined;

interface GuidancePanelProps {
    step: AiReadingStep;
    blocker: AiReadingBlocker;
    noMatch?: NoMatchInfo | null;
    // Pluralizes count-sensitive instructions (currently the confirm step); pass the register
    // count of the matched job there, leave undefined elsewhere.
    registersTotal?: number;
    degraded: boolean;
    // False while another layer carries the instruction (alignment hint below the frame, or a
    // bottom panel) — then the text stays screen-reader-only so it is announced exactly once on
    // screen. Deliberate: the top area is regularly covered by browser permission banners, so it
    // must never be the sole carrier of an important instruction.
    instructionVisible: boolean;
    muted: boolean;
    onToggleMute: () => void;
}

const GuidancePanel = ({ step, blocker, noMatch, registersTotal, degraded, instructionVisible, muted, onToggleMute }: GuidancePanelProps) => {
    const { t } = useTranslation();
    const stepNumber = STEP_NUMBER[step];
    const instructionKey = instructionKeyFor(step, blocker, noMatch);
    const instruction =
        registersTotal !== undefined ? t(instructionKey, { count: registersTotal }) : t(instructionKey, instructionParamsFor(noMatch, t));

    return (
        // right-14 keeps the row clear of the screen-global close button rendered at top-2 right-2.
        <div className={'absolute top-0 left-0 right-14 p-2 flex items-start gap-2 pointer-events-none'}>
            {instructionVisible ? (
                <div className={'flex-1 bg-black bg-opacity-60 text-white rounded-lg px-3 py-2'}>
                    {stepNumber && (
                        <div data-testid="step-indicator" className={'text-xs opacity-80'}>
                            {t('aiReading.stepIndicator', { step: stepNumber, total: 4 })}
                        </div>
                    )}
                    <div data-testid="guidance-instruction" aria-live="polite" className={'text-base'}>
                        {instruction}
                    </div>
                    {degraded && <div className={'text-xs opacity-70 mt-1'}>{t('aiReading.degradedHint')}</div>}
                </div>
            ) : (
                <div className={'flex-1'}>
                    <div data-testid="guidance-instruction" aria-live="polite" className={'sr-only'}>
                        {instruction}
                    </div>
                    {degraded && (
                        <div className={'inline-block text-xs text-white bg-black bg-opacity-60 rounded px-2 py-1'}>{t('aiReading.degradedHint')}</div>
                    )}
                </div>
            )}
            <button
                data-testid="mute-toggle"
                type="button"
                aria-label={t(muted ? 'aiReading.buttons.unmute' : 'aiReading.buttons.mute')}
                onClick={onToggleMute}
                className={'pointer-events-auto bg-black bg-opacity-60 text-white rounded-full p-3'}
            >
                <Icon path={muted ? Icon.Path.mdiVolumeOff : Icon.Path.mdiVolumeHigh} size={'22px'} />
            </button>
        </div>
    );
};

export default GuidancePanel;
